import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Scale, Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/hooks/useSiteContent";

const SOCIAL_LINKS = [
  { key: "facebook_url", label: "Facebook", Icon: Facebook, testId: "footer-social-facebook" },
  { key: "twitter_url", label: "X (Twitter)", Icon: X, testId: "footer-social-twitter" },
  { key: "instagram_url", label: "Instagram", Icon: Instagram, testId: "footer-social-instagram" },
  { key: "youtube_url", label: "YouTube", Icon: Youtube, testId: "footer-social-youtube" },
  { key: "linkedin_url", label: "LinkedIn", Icon: Linkedin, testId: "footer-social-linkedin" },
];

export const Footer = () => {
  const { t, L } = useLanguage();
  const { content } = useSiteContent();

  // Memoise the filtered social-link list — only recomputes when `content` changes.
  const activeSocialLinks = useMemo(
    () => SOCIAL_LINKS.filter((s) => content?.[s.key]),
    [content],
  );

  return (
    <footer className="mt-20 border-t bg-secondary/40">
      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Scale className="h-5 w-5" />
            </span>
            <span className="font-serif text-lg font-bold">{L(content, "org_name") || "Litigation India"}</span>
          </div>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">{t("footer_tagline")}</p>
          <div className="mt-4 space-y-1 text-sm text-muted-foreground">
            {content?.contact_email && (
              <p className="flex items-center gap-2"><Mail className="h-4 w-4" /> {content.contact_email}</p>
            )}
            {content?.contact_phone && (
              <p className="flex items-center gap-2"><Phone className="h-4 w-4" /> {content.contact_phone}</p>
            )}
            {content?.address && (
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {content.address}</p>
            )}
          </div>
          {activeSocialLinks.length > 0 && (
            <div className="mt-5 flex items-center gap-2.5" data-testid="footer-social-links">
              {activeSocialLinks.map(({ key, label, Icon, testId }) => (
                <a
                  key={key}
                  href={content[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={testId}
                  className="flex h-9 w-9 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="font-serif text-base font-semibold">{t("quick_links")}</h4>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/updates" className="hover:text-primary">{t("nav_updates")}</Link></li>
            <li><Link to="/advice" className="hover:text-primary">{t("nav_advice")}</Link></li>
            <li><Link to="/supreme-court-lawyers" className="hover:text-primary">{t("nav_lawyers")}</Link></li>
            <li><Link to="/resources" className="hover:text-primary">{t("nav_resources_full")}</Link></li>
            <li><Link to="/insights" className="hover:text-primary">{t("nav_insights")}</Link></li>
            <li><Link to="/team" className="hover:text-primary">{t("nav_team")}</Link></li>
            <li><Link to="/about" className="hover:text-primary">{t("nav_about")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-base font-semibold">{t("our_mission")}</h4>
          <p className="mt-3 text-sm text-muted-foreground line-clamp-5">{L(content, "mission")}</p>
        </div>
      </div>
      <div className="border-t">
        <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6">
          <p className="text-[11px] leading-relaxed text-muted-foreground" data-testid="footer-disclaimer">
            {t("disclaimer")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">© {new Date().getFullYear()} Litigation India. {t("rights")}</p>
        </div>
      </div>
    </footer>
  );
};
